import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Share, PlusSquare, MoreVertical, Download, Smartphone, CheckCircle2 } from 'lucide-react';

const IOS_STEPS = [
  { icon: Smartphone, text: <>Öffnen Sie das Portal in <b>Safari</b> (in anderen Browsern ist die Installation auf dem iPhone nicht möglich).</> },
  { icon: Share, text: <>Tippen Sie unten in der Leiste auf <b>Teilen</b> (Quadrat mit Pfeil nach oben).</> },
  { icon: PlusSquare, text: <>Wählen Sie <b>„Zum Home-Bildschirm"</b> – ggf. etwas nach unten scrollen.</> },
  { icon: CheckCircle2, text: <>Oben rechts auf <b>„Hinzufügen"</b> tippen. Fertig – das Portal liegt als App mit unserem Logo auf Ihrem Startbildschirm.</> },
];

const ANDROID_STEPS = [
  { icon: Smartphone, text: <>Öffnen Sie das Portal in <b>Chrome</b> (oder Samsung Internet / Edge).</> },
  { icon: MoreVertical, text: <>Tippen Sie oben rechts auf das <b>Menü ⋮</b>.</> },
  { icon: Download, text: <>Wählen Sie <b>„App installieren"</b> bzw. <b>„Zum Startbildschirm hinzufügen"</b>.</> },
  { icon: CheckCircle2, text: <>Mit <b>„Installieren"</b> bestätigen – die App erscheint auf dem Startbildschirm und in der App-Übersicht.</> },
];

const detectTab = () => (/android/i.test(navigator.userAgent) ? 'android' : 'ios');

export const PwaInstallGuide = ({ onClose, onNever, promptEvent, onInstall }) => {
  const [tab, setTab] = useState(detectTab);
  const steps = tab === 'ios' ? IOS_STEPS : ANDROID_STEPS;
  return createPortal(
    <div className="fixed inset-0 z-[99990] bg-black/55 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="pwa-guide">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto pb-[env(safe-area-inset-bottom)]" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="bg-brand-navy text-white px-5 pt-5 pb-6 rounded-t-2xl relative">
          <button onClick={onClose} aria-label="Schließen" data-testid="pwa-guide-close" className="absolute top-3 right-3 p-1.5 rounded-full text-white/70 hover:bg-white/10"><X className="w-5 h-5" /></button>
          <div className="flex items-center gap-4">
            <img src="/icons/icon-192.png?v=3" alt="" className="w-16 h-16 rounded-[22%] shadow-lg ring-1 ring-white/20" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-brand-gold font-semibold">Kundenportal</p>
              <h3 className="font-heading text-xl font-bold leading-tight">Ihr Kundenportal als App</h3>
              <p className="text-xs text-white/70 mt-0.5">Direkt vom Startbildschirm, ohne App Store – in 30 Sekunden eingerichtet.</p>
            </div>
          </div>
        </div>
        <div className="px-5 pt-4">
          <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-gray-100 text-sm font-medium" role="tablist">
            {[['ios', 'iPhone / iPad'], ['android', 'Android']].map(([k, l]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`pwa-guide-tab-${k}`}
                className={`h-9 rounded-lg transition-colors ${tab === k ? 'bg-white text-brand-navy shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
            ))}
          </div>
          <ol className="mt-4 space-y-3" data-testid={`pwa-guide-steps-${tab}`}>
            {steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="relative shrink-0 w-9 h-9 rounded-xl bg-brand-navy/5 text-brand-navy flex items-center justify-center">
                  <s.icon className="w-4.5 h-4.5 w-[18px] h-[18px]" />
                  <span className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-brand-gold text-brand-navy text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                </span>
                <p className="text-sm text-gray-700 leading-relaxed pt-1.5">{s.text}</p>
              </li>
            ))}
          </ol>
          {tab === 'android' && promptEvent && (
            <button onClick={onInstall} data-testid="pwa-guide-install" className="mt-4 w-full h-11 rounded-xl bg-brand-gold text-brand-navy font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-brand-gold-light">
              <Download className="w-4 h-4" /> Jetzt direkt installieren
            </button>
          )}
        </div>
        <div className="px-5 py-4 mt-2 flex items-center justify-between gap-3 border-t border-gray-100">
          <button onClick={onNever} data-testid="pwa-guide-never" className="text-xs text-gray-400 hover:text-gray-600 underline-offset-2 hover:underline">Nicht mehr anzeigen</button>
          <button onClick={onClose} data-testid="pwa-guide-done" className="h-10 px-5 rounded-lg bg-brand-navy text-white text-sm font-medium">Verstanden</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PwaInstallGuide;
